"use client";

import { useCallback, useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import getConfigTID from "@/lib/getConfigTID";
import { refreshAuthTokenAsync } from "@/lib/refreshAuthToken";
import { useLogin } from "@/context/login/LoginContext";
import type { LoginDispatch } from "@/context/login/LoginContext";
import type { LoginState } from "@/context/login/reducer";

/** Refresh this far ahead of `exp` so a token can't expire mid-request. */
const EXPIRY_MARGIN_MS = 60_000;

/** Scope the agent service requires; without it every call returns 403. */
const REQUIRED_SCOPE = "agents";
function isFresh(token: string): boolean {
  try {
    const {
      exp
    } = jwtDecode<{
      exp?: number;
    }>(token);
    if (!exp) return false;
    return exp * 1000 - Date.now() > EXPIRY_MARGIN_MS;
  } catch {
    return false;
  }
}

/**
 * A token missing `agents` fails as an opaque "Failed to load agent" inside the
 * iframe, so name the cause in the console rather than leaving it to a network
 * trace. Development only.
 */
function warnIfUnusable(token: string): void {
  if (process.env.NODE_ENV === "production") return;
  if (!token) {
    console.warn("[Assist] No access token available. Sign in, or pass ?_fda_token= for a manual test.");
    return;
  }
  try {
    const {
      scope
    } = jwtDecode<{
      scope?: string;
    }>(token);
    if (!scope?.split(" ").includes(REQUIRED_SCOPE)) {
      console.warn(`[Assist] Access token has scope "${scope ?? "(none)"}" and is missing "${REQUIRED_SCOPE}". ` + "Trimble IAM must grant that scope to this TID client before the agent will load.");
    }
  } catch {
    console.warn("[Assist] Access token is not a decodable JWT.");
  }
}

/**
 * Builds the `provideToken` callback for the Trimble Assist chat.
 *
 * Assist calls this on every authenticated request for the lifetime of the
 * iframe, which outlives a one-hour TID access token, so an expired token is
 * exchanged via the refresh grant before being handed over.
 */
export function useAssistToken(): () => Promise<string> {
  const [loginState, loginDispatch] = useLogin() ?? [];

  // Assist keeps the first callback it receives, so auth is read through refs:
  // a plain closure would freeze on whatever token existed at mount and never
  // observe a later login or refresh.
  const loginRef = useRef<LoginState | undefined>(loginState);
  loginRef.current = loginState;
  const dispatchRef = useRef<LoginDispatch | undefined>(loginDispatch);
  dispatchRef.current = loginDispatch;
  const refreshRef = useRef<Promise<string> | null>(null);

  // Captured at mount rather than read on demand so a later navigation that
  // rewrites the query string can't strip the manual override mid-session.
  const overrideRef = useRef<string | null>(null);
  useEffect(() => {
    overrideRef.current = new URLSearchParams(window.location.search).get("_fda_token");
  }, []);
  return useCallback(async () => {
    const resolve = (token: string | null | undefined) => {
      const resolved = token || overrideRef.current || "";
      warnIfUnusable(resolved);
      return resolved;
    };
    const token_0 = loginRef.current?.token;
    if (token_0 && isFresh(token_0)) return resolve(token_0);
    const refreshToken = loginRef.current?.refreshToken;
    // Nothing to exchange: hand back whatever we have and let Assist surface
    // the auth error rather than silently sending an empty string.
    if (!refreshToken) return resolve(token_0);

    // Assist fires several requests at once when the chat opens; collapse them
    // onto a single refresh so we don't burn the refresh token in parallel.
    refreshRef.current ??= (async () => {
      try {
        const {
          client_id,
          redirect_uri,
          code_verifier
        } = getConfigTID();
        const authData = await refreshAuthTokenAsync({
          refreshToken,
          client_id,
          redirect_uri,
          code_verifier,
          path: window.location.href
        });
        if (!authData?.id) return resolve(null);
        dispatchRef.current?.({
          type: "SET_LOGIN",
          payload: {
            token: authData.id,
            authTime: String(authData.time),
            idToken: authData.idTokenForLogout ?? "",
            refreshToken: authData.refresh_token ?? ""
          }
        });
        return resolve(authData.id);
      } finally {
        refreshRef.current = null;
      }
    })();
    return refreshRef.current;
  }, []);
}